import { useState } from "react";
import { Copy, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * How many files were folded into an original because their bytes are the
 * same (2026-10-03, the owner). Until then a copy was folded with nothing to
 * tell anyone.
 *
 * Closable. Closing is remembered in this browser with the count it closed
 * at, so the line comes back when another copy is found, and not before.
 * Storage can be missing or refuse (a private window); the line then shows
 * again on the next visit, which is the safe way to be wrong.
 */
export function DuplicatesLine({
  projectId,
  duplicates,
  className,
}: {
  projectId: string;
  duplicates: number;
  className?: string;
}) {
  const key = `gb:duplicates-closed:${projectId}`;
  const [closedAt, setClosedAt] = useState<number>(() => readClosed(key));
  if (duplicates === 0 || duplicates <= closedAt) return null;

  const close = () => {
    setClosedAt(duplicates);
    try {
      localStorage.setItem(key, String(duplicates));
    } catch {
      // Not remembered; see above.
    }
  };

  return (
    <section
      aria-label="Duplicates"
      className={cn("flex items-center gap-3 rounded-card-lg border-[1.5px] border-border bg-subtle px-4 py-3", className)}
    >
      <Copy size={16} aria-hidden className="shrink-0 text-muted" />
      <p className="min-w-0 flex-1 text-[0.86rem] text-ink-slate">
        {duplicates === 1
          ? "1 duplicate document identified. We have deduplicated it for you."
          : `${duplicates} duplicate documents identified. We have deduplicated these for you.`}
      </p>
      <button
        type="button"
        aria-label="Close"
        onClick={close}
        className="shrink-0 rounded p-1 text-muted transition-colors hover:text-ink"
      >
        <X size={16} />
      </button>
    </section>
  );
}

function readClosed(key: string): number {
  try {
    const value = Number(localStorage.getItem(key));
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}
